import { useEffect, useRef, useState } from "react";
import {
    Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Slide, TextField, Typography, useMediaQuery,
    Tooltip, Backdrop, CircularProgress, Snackbar, Alert, Link, Dialog, DialogContentText, DialogContent, AlertColor,
} from "@mui/material"
import usePageTitle from "../hooks/usePageTitle"

import './Rsvp.css';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useSearchParams } from "react-router-dom";
import {
    FoodPreference, Person, PostRsvpResponse, RsvpReply
} from "../api/RsvpApi";
import { LinkBehavior } from "../behaviors/LinkBehavior";
import React from "react";

type IncompletePerson = Omit<Person, 'needsHotel' | 'joinsBreakfast'>;

function usePageSearchParam(startPage: number): [number, (_: number) => void] {
    const [search, setSearch] = useSearchParams({ page: startPage.toString() })
    const searchAsObject = Object.fromEntries(
        new URLSearchParams(search)
    );

    function setPage(page: number) {
        setSearch({ page: page.toString() })
    }

    return [Number.parseInt(searchAsObject["page"], 10), setPage];
}

export default function Rsvp() {

    const STEP_HELLO = 0;
    const STEP_RSVP = 1;
    const STEP_MEAL_CHOICE = 2;
    const STEP_ADD_GUEST = 3;
    const STEP_ADDITIONAL_GUEST_NAME = 4;
    const STEP_HOTEL = 5;
    const STEP_BREAKFAST = 6;
    const STEP_REVIEW = 7;
    const STEP_THANK_YOU = 8;
    const STEP_NOT_GOING = 9;

    const mealChoices: Record<FoodPreference, string> = {
        Vegan: 'Vegan',
        Vegetarian: 'Vegetarisch',
        Meat: 'Alle Speisen',
    };

    type MealChoice = keyof typeof mealChoices;

    const [step, setStep] = usePageSearchParam(STEP_HELLO);
    const [loading, setLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const [showRsvpForbiddenAlert, setShowRsvpForbiddenAlert] = useState(false);

    // const [mainGuestFirstName, setMainGuestFirstName] = useState('');
    // const [mainGuestLastName, setMainGuestLastName] = useState('');

    // List of all people added so far
    const [people, setPeople] = useState<IncompletePerson[]>([]);

    // State for adding the current person
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [mealChoice, setMealChoice] = useState<MealChoice | ''>('');
    const [allergies, setAllergies] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    // State for RSVP-level choices (valid for all guests)
    const [isGoing, setIsGoing] = useState<boolean | undefined>(undefined);
    const [needsHotel, setNeedsHotel] = useState<boolean | undefined>(undefined);
    const [joinsBreakfast, setJoinsBreakfast] = useState<boolean | undefined>(undefined);

    const lastNameRef = useRef<HTMLInputElement>();
    const lastNameAlertRef = useRef<HTMLInputElement>();

    const [dirIn, dirOut]: ['up' | 'left', 'right' | 'down'] = useMediaQuery('(min-width: 600px)')
        ? ['up', 'down']
        : ['left', 'right'];

    usePageTitle('💌 RSVP');

    if(step > 0 && people.length === 0 && !firstName && !lastName) {
        setStep(STEP_HELLO);
    }

    function postRsvp(): Promise<void> {

        if (loading) {
            console.log('lade noch, breche ab');
            return Promise.reject();
        }

        console.log('Starte RSVP-Request');
        setShowRsvpForbiddenAlert(false);
        setShowSnackbar(false);
        setLoading(true);

        return axios.post<PostRsvpResponse>('/api/rsvp/rsvp', {
            rsvp: isGoing ? 'Going' : 'NotGoing',
            people: people.map(p => ({...p, needsHotel: needsHotel ?? null, joinsBreakfast: joinsBreakfast ?? null})),
        } as RsvpReply)
            .then((response: AxiosResponse<PostRsvpResponse>) => {
                setShowSnackbar(true);
                setSnackbarSeverity('success');
                setSnackbarMessage('Antwort erfolgreich versendet.')

                if (!isGoing) {
                    return;
                }

                setStep(STEP_THANK_YOU);
            })
            .catch((error: AxiosError) => {
                if (error.response!.status === 403) {
                    setShowRsvpForbiddenAlert(true);
                } else {
                    setShowSnackbar(true);
                    setSnackbarSeverity('error');
                    setSnackbarMessage(`Ein unerwarteter Fehler ist aufgetreten: ${JSON.stringify(error.response?.data)}`)
                }

                return Promise.reject();
            })
            .finally(() => setLoading(false));
    }

    function goToRsvp() {
        if (!!firstName && !!lastName) {
            setStep(STEP_RSVP);
        }
    }

    useEffect(() => {
        if (isGoing === false) {
            postRsvp()
                .then(() => setStep(STEP_NOT_GOING));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGoing]);

    return <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'auto',
        gridTemplateRows: 'auto',
        textAlign: 'center',
    }} m="auto" pt="1rem">
        <Backdrop
            sx={{
                color: '#fff',
                zIndex: (theme) => theme.zIndex.drawer + 1
            }}
            open={loading}>
            <CircularProgress color="inherit" />
        </Backdrop>

        <Snackbar
            open={showSnackbar}
            autoHideDuration={snackbarSeverity === 'success' ? 6000 : null}
            onClose={() => setShowSnackbar(false)}
        >
            <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
        </Snackbar>

        <Dialog open={showRsvpForbiddenAlert} onClose={() => setShowRsvpForbiddenAlert(false)}>
            <DialogContent>
                <DialogContentText>
                    <Typography>
                        Leider konnten wir keine offene Einladung auf der Gästeliste finden. Das kann mehrere Gründe haben:
                    </Typography>

                    <ul>
                        <li>Du hast uns bereits eine Antwort geschickt</li>
                        <li>Du hast dich bei deinem Namen vertippt (oder wir haben uns vertippt!)</li>
                        <li>Du stehst nicht auf der Gästeliste 🫥</li>
                    </ul>

                    <Typography>
                        Bitte prüfe, dass Du Deinen Vor- und Nachnamen richtig geschrieben und keinen Spitznamen verwendet
                        hast. Wenn es immer noch nicht funktionieren sollte, schreib uns bei WhatsApp an!
                    </Typography>
                </DialogContentText>

                <Grid container spacing={2} sx={{ mt: '1rem', mb: '1rem' }}>
                    <Grid item md={6} xs={12} >
                        <TextField
                            autoFocus
                            fullWidth
                            label="Vorname"
                            value={firstName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.currentTarget.value)}
                            onKeyDown={(e : React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') lastNameAlertRef.current?.focus() }}
                        />
                    </Grid>
                    <Grid item md={6} xs={12} >
                        <TextField
                            fullWidth
                            label="Nachname"
                            inputRef={lastNameAlertRef}
                            value={lastName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.currentTarget.value)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') postRsvp() }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => postRsvp()}
                            sx={{ marginBottom: '1rem' }}>
                            Absenden
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="outlined"
                            fullWidth
                            onClick={() => setShowRsvpForbiddenAlert(false)}
                            sx={{ marginBottom: '1rem' }}
                        >
                            Schließen
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>

        <Slide in={step === STEP_HELLO} direction={step === STEP_HELLO ? dirIn : dirOut} >
            <Box className="center-in-grid" sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography
                    color="primary"
                    variant="h5" mb="1rem">
                    Schön, dass ihr mit uns feiern wollt!
                </Typography>

                <Typography mb="1rem">
                    Bevor wir starten können, brauchen wir deinen Namen:
                </Typography>

                <Grid container spacing={2} sx={{ mb: '1rem' }}>
                    <Grid item md={6} xs={12} >
                        <TextField
                            autoFocus
                            fullWidth
                            label="Vorname"
                            value={firstName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.currentTarget.value)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') lastNameRef.current?.focus() }}
                        />
                    </Grid>
                    <Grid item md={6} xs={12} >
                        <TextField
                            fullWidth
                            label="Nachname"
                            inputRef={lastNameRef}
                            value={lastName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.currentTarget.value)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') goToRsvp() }}
                        />
                    </Grid>
                </Grid>

                <Tooltip title={!firstName || !lastName
                    ? 'Bitte gib Deinen Vor- und Nachnamen ein, damit wir dich zuordnen können.'
                    : undefined
                }>
                    <div>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={!firstName || !lastName}
                            onClick={() => goToRsvp()}>
                            Weiter
                        </Button>
                    </div>
                </Tooltip>

            </Box>
        </Slide>

        <Slide in={step === STEP_RSVP} direction={step === STEP_RSVP ? dirIn : dirOut}>
            <Box className="center-in-grid" sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography color="primary" variant="h5" mb="1rem">
                    Hallo {firstName}!
                </Typography>

                <Typography>
                    Wir feiern am 08.08.2026 unsere Hochzeit im Gastwerk Hotel in Hamburg und möchten dich gerne dabei haben!
                </Typography>

                <Typography mb="1rem">
                    Wirst du/Werdet ihr dabei sein?
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                setIsGoing(true);
                                setStep(STEP_MEAL_CHOICE);
                            }}
                        >
                            Ich bin dabei! 
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                people.push({ firstName, lastName, allergies: '', foodPreference: 'Vegetarian' });

                                // setIsGoing(false) triggert einen Effekt, der das RSVP abschickt
                                setIsGoing(false);
                            }}
                        >
                            Ich kann leider nicht
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Slide>

        <Slide in={step === STEP_MEAL_CHOICE} direction={step === STEP_MEAL_CHOICE ? dirIn : dirOut}>
            <Box className="center-in-grid">
                <Typography color="primary" variant="h5" mb="1rem">
                    { people.length === 0 
                        ? 'Was möchtest du essen?'
                        : `Was isst ${firstName}?` 
                    }
                </Typography>

                <Typography mb="1rem">
                    { people.length === 0 
                        ? 'Ernährst du dich vegan, vegetarisch oder isst du auch Fleisch?'
                        : `Ernährt sich ${firstName} vegan, vegetarisch oder steht auch Fleisch auf dem Speiseplan?` 
                    }
                </Typography>
                <Typography mb="1rem">
                    Bitte lass uns auch wissen, ob irgendwelche Allergien, Lebensmittelunverträglichkeiten
                    oder ähnliches vorliegen, damit wir dies bei der Auswahl der Gerichte für das Buffet berücksichtigen können!
                </Typography>

                <Box>
                    <Typography color="secondary" variant="h6" textAlign="left" mb="0.5rem">
                        {firstName || "Du"}
                    </Typography>
                    <FormControl fullWidth size="small" >
                        <InputLabel id="meal-choice">Essen für {firstName}</InputLabel>
                        <Select
                            labelId="meal-choice"
                            label={`Essen für ${firstName}`}
                            sx={{ textAlign: 'left', marginBottom: '1rem' }}
                            value={mealChoice}
                            onChange={e => setMealChoice(e.target.value as MealChoice)}
                        >
                            {
                                Object.entries(mealChoices)
                                    .map(([key, text]) => <MenuItem key={key} value={key}>
                                        {text}
                                    </MenuItem>)
                            }
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        fullWidth
                        value={allergies}
                        onChange={({ target: { value } }) => setAllergies(value)}
                        label="Allergien und Unverträglichkeiten"
                        sx={{ marginBottom: '1rem' }}
                    />

                    <Tooltip title={!mealChoice
                        ? 'Bitte wähle deine Essensauswahl aus.'
                        : undefined
                    }>
                        <div>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => {
                                    people.push({ firstName, lastName, foodPreference: mealChoice as MealChoice, allergies });
                                    setStep(isEditing ? STEP_REVIEW : STEP_ADD_GUEST);
                                }}
                                disabled={!mealChoice}
                                sx={{ marginBottom: "1rem" }}
                            >
                                Weiter
                            </Button>
                        </div>
                    </Tooltip>
                </Box>
            </Box>
        </Slide>

        <Slide in={step === STEP_ADD_GUEST} direction={step === STEP_ADD_GUEST ? dirIn : dirOut}>
            <Box className="center-in-grid">
                <Typography color="primary" variant="h5" mb="1rem">
                    Möchtest du noch jemanden mitbringen?
                </Typography>

                <Typography mb="1rem">
                    Du kannst weitere Personen hinzufügen, die mit dir zusammen feiern möchten.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                setFirstName('');
                                setLastName('');
                                setMealChoice('');
                                setAllergies('');
                                setStep(STEP_ADDITIONAL_GUEST_NAME);
                            }}
                        >
                            Eine weitere Person hinzufügen
                        </Button>
                    </Grid>
                     <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                setStep(STEP_HOTEL);
                            }}
                        >
                            {people.length > 1 ? 'Ich habe alle Gäste eingeladen' : 'Nein, ich komme alleine'}
                        </Button>
                    </Grid>
                </Grid>

            </Box>
        </Slide>

        <Slide in={step === STEP_ADDITIONAL_GUEST_NAME} direction={step === STEP_ADDITIONAL_GUEST_NAME ? dirIn : dirOut}>
            <Box className="center-in-grid" sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography
                    color="primary"
                    variant="h5" mb="1rem">
                    Wie heißt deine Begleitung?
                </Typography>

                <Grid container spacing={2} sx={{ mb: '1rem' }}>
                    <Grid item md={6} xs={12} >
                        <TextField
                            autoFocus
                            fullWidth
                            label="Vorname"
                            value={firstName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.currentTarget.value)}
                            onKeyDown={(e : React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') lastNameRef.current?.focus() }}
                        />
                    </Grid>
                    <Grid item md={6} xs={12} >
                        <TextField
                            fullWidth
                            label="Nachname"
                            inputRef={lastNameRef}
                            value={lastName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.currentTarget.value)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') setStep(STEP_MEAL_CHOICE) }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Tooltip title={!firstName || !lastName
                            ? 'Bitte gib den Vor- und Nachnamen Deiner Begleitung ein.'
                            : undefined
                        }>
                            <div>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={!firstName || !lastName}
                                    onClick={() => setStep(STEP_MEAL_CHOICE)}>
                                    Weiter
                                </Button>
                            </div>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>
        </Slide>

        <Slide in={step === STEP_HOTEL} direction={step === STEP_HOTEL ? dirIn : dirOut}>
            <Box className="center-in-grid">
                <Typography color="primary" variant="h5" mb="1rem">
                    Übernachtung im Hotel
                </Typography>

                <Typography mb="1rem">
                    Für einen besseren Überblick über die Anzahl der benötigten Zimmer würden wir gerne wissen, ob {people.length > 1 ? 'ihr' : 'du'} im <Typography color="primary" component="span">Gastwerk Hotel</Typography> übernachten {people.length > 1 ? 'möchtet' : 'möchtest'}.
                </Typography>

                <Typography mb="1rem">
                    <Typography color="primary" component="span" fontWeight="bold">Wichtig:</Typography> Wir buchen <strong>nicht</strong> die Hotel-Zimmer für euch - das müsst ihr selbst tun. 
                        Weitere Informationen zum Hotel und zur Buchung {people.length > 1 ? 'findet ihr' : 'findest du'} auf der Seite <Link href="/info" component={LinkBehavior} target="_blank">Allgemeine Infos zur Hochzeit</Link>.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => {
                            setNeedsHotel(true);
                            setStep(isEditing ? STEP_REVIEW : STEP_BREAKFAST);
                        }}>
                            Ja, {people.length > 1 ? 'wir möchten' : 'ich möchte'} im Gastwerk Hotel übernachten
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Button fullWidth variant="outlined" onClick={() => {
                            setNeedsHotel(false)
                            setStep(isEditing ? STEP_REVIEW : STEP_BREAKFAST);
                        }}>
                            Nein, {people.length > 1 ? 'wir brauchen' : 'ich brauche'} kein Zimmer
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Button fullWidth variant="outlined" onClick={() => {
                            setNeedsHotel(undefined);
                            setStep(isEditing ? STEP_REVIEW : STEP_BREAKFAST);
                        }}>
                            {people.length > 1 ? 'Wir wissen' : 'Ich weiß'} es noch nicht
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Slide>

        <Slide in={step === STEP_BREAKFAST} direction={step === STEP_BREAKFAST ? dirIn : dirOut}>
            <Box className="center-in-grid">
                <Typography color="primary" variant="h5" mb="1rem">
                    Frühstück am nächsten Morgen
                </Typography>

                <Typography mb="1rem">
                    Am nächsten Morgen {people.length > 1 ? 'könnt ihr' : 'kannst du'} gemeinsam mit uns im Gastwerk Hotel frühstücken. Bitte {people.length > 1 ? 'lasst' : 'lass'} uns wissen, ob {people.length > 1 ? 'ihr' : 'du'} daran teilnehmen {people.length > 1 ? 'möchtet' : 'möchtest'}.
                </Typography>

                <Typography mb="1rem">
                    Die Teilnahme am Frühstück ist übrigens auch für diejenigen möglich, die nicht im Hotel übernachten - das Frühstück kostet dann 26€ pro Person.
                </Typography>


                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => {
                            setJoinsBreakfast(true);
                            setStep(STEP_REVIEW);
                        }}>
                            Ja, {people.length > 1 ? 'wir möchten' : 'ich möchte'} am Frühstück teilnehmen
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Button fullWidth variant="outlined" onClick={() => {
                            setJoinsBreakfast(false);
                            setStep(STEP_REVIEW);
                        }}>
                            Nein, {people.length > 1 ? 'wir sind' : 'ich bin'} nicht dabei
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Button fullWidth variant="outlined" onClick={() => {
                            setJoinsBreakfast(undefined);
                            setStep(STEP_REVIEW);
                        }}>
                            {people.length > 1 ? 'Wir wissen' : 'Ich weiß'} es noch nicht
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Slide>

        <Slide in={step === STEP_REVIEW} direction={step === STEP_REVIEW ? dirIn : dirOut}>
            <Box className="center-in-grid">
                <Typography variant="h5" color="primary" mb="1rem">
                    Wir sind fast fertig!
                </Typography>

                <Typography mb="1rem">
                    Bitte überprüfe noch kurz deine Eingaben. Wenn alles richtig aussieht, kann's losgehen!
                </Typography>

                <Grid container textAlign="left" mb="1rem">
                    <Grid item xs={4} textAlign="left">
                        <Typography color="primary">
                            Name
                        </Typography>
                    </Grid>

                    <Grid item xs={3} textAlign="left">
                        <Typography color="primary">
                            Essensauswahl
                        </Typography>
                    </Grid>

                    <Grid item xs={3} textAlign="left">
                        <Typography color="primary">
                            Allergien
                        </Typography>
                    </Grid>

                    <Grid item xs={2} textAlign="left">
                    </Grid>

                    {
                        people.map((person, index) => <React.Fragment key={index}>
                            <Grid item xs={4}>{person.firstName} {person.lastName}</Grid>
                            <Grid item xs={3}>{person.foodPreference ? mealChoices[person.foodPreference] : ''}</Grid>
                            <Grid item xs={3}>{person.allergies !== '' ? person.allergies : 'Keine'}</Grid>

                            <Grid item xs={2}>
                                <Button variant="text" style={{ paddingTop: 0 }} onClick={() => {
                                    setFirstName(person.firstName);
                                    setLastName(person.lastName);
                                    setMealChoice(person.foodPreference || '');
                                    setAllergies(person.allergies || '');
                                    setStep(STEP_MEAL_CHOICE);
                                    setIsEditing(true);

                                    // Entferne die Person, die gerade bearbeitet werden soll, aus der Liste der Gäste, damit die Änderungen nicht doppelt eingetragen werden
                                    setPeople(people.filter((_, i) => i !== index));
                                }}>
                                    Bearbeiten
                                </Button>
                            </Grid>
                        </React.Fragment>)
                    }

                    <Button variant="text" onClick={() => setStep(STEP_ADD_GUEST)} sx={{ margin: 'auto' }}>
                        Weitere Gäste hinzufügen
                    </Button>
                </Grid>

                <Typography mb="1rem">
                    { needsHotel === undefined && `❓ ${people.length > 1 ? 'Wir sind' : 'Ich bin'} noch unsicher wegen der Hotel-Übernachtung.` }
                    { needsHotel === true && `🏨 ${people.length > 1 ? 'Wir übernachten' : 'Ich übernachte'} im Gastwerk Hotel.` }
                    { needsHotel === false && `🏠 ${people.length > 1 ? 'Wir haben' : 'Ich habe'} kein Zimmer im Gastwerk Hotel.` }

                    <Button variant="text" onClick={() => {
                        setIsEditing(true);
                        setStep(STEP_HOTEL);
                    }} sx={{ marginLeft: '1rem' }}>
                        Bearbeiten
                    </Button>
                </Typography>

                <Typography mb="1rem">
                    { joinsBreakfast === undefined && `❓ ${people.length > 1 ? 'Wir sind' : 'Ich bin'} noch unsicher wegen der Teilnahme am Frühstück.` }
                    { joinsBreakfast === true && `🥐 ${people.length > 1 ? 'Wir nehmen' : 'Ich nehme'} am Frühstück teil.` }
                    { joinsBreakfast === false && `🍞 ${people.length > 1 ? 'Wir nehmen' : 'Ich nehme'} nicht am Frühstück teil.` }

                    <Button variant="text" onClick={() => {
                        setIsEditing(true);
                        setStep(STEP_BREAKFAST);
                    }} sx={{ marginLeft: '1rem' }}>
                        Bearbeiten
                    </Button>
                </Typography>

                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => postRsvp()}
                    sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
                    Absenden
                </Button>
            </Box>
        </Slide>

        <Slide in={step === STEP_THANK_YOU} direction={step === STEP_THANK_YOU ? dirIn : dirOut} >
            <Box className="center-in-grid">
                <Typography variant="h5" color="primary" mb="1rem">
                    Vielen Dank für {people.length > 1 ? 'Eure' : 'Deine'} Antwort!
                </Typography>

                <Typography>
                    {people.length > 1
                        ? <>
                            Sofern Ihr im
                            <Typography color="primary" component="span"> Gastwerk&nbsp;Hotel </Typography>
                            übernachten wollt, denkt bitte
                            <Typography color="primary" component="span"> dringend </Typography>
                            daran, bis zum
                            <Typography color="primary" component="span"> 01.&nbsp;07.</Typography> mit dem Stichwort
                            <Typography color="primary" component="span">"Hochzeit Lindner/Scheffel"</Typography> ein Zimmer zu buchen.

                            <Typography>
                                Weitere Informationen findet Ihr auf der Seite<span> </span>
                                <Link href="/info" component={LinkBehavior} target="_blank">
                                    Allgemeine Infos zur Hochzeit
                                </Link>.
                            </Typography>
                        </>
                        : <>
                            Sofern Du im
                            <Typography color="primary" component="span"> Gastwerk&nbsp;Hotel </Typography>
                            übernachten willst, denk bitte
                            <Typography color="primary" component="span"> dringend </Typography>
                            daran, bis zum
                            <Typography color="primary" component="span"> 01.&nbsp;07.</Typography> mit dem Stichwort
                            <Typography color="primary" component="span">"Hochzeit Lindner/Scheffel"</Typography> ein Zimmer zu buchen.

                            <Typography>
                                Weitere Informationen findest Du auf der Seite<span> </span>
                                <Link href="/info" component={LinkBehavior} target="_blank">
                                    Allgemeine Infos zur Hochzeit
                                </Link>.
                            </Typography>
                        </>}
                </Typography>
            </Box>
        </Slide>

        <Slide in={step === STEP_NOT_GOING} direction={step === STEP_NOT_GOING ? dirIn : dirOut} >
            <Box className="center-in-grid">
                <Typography variant="h5" color="primary" mb="1rem">
                    Schade, dass du nicht kommen kannst, {firstName}! 😞
                </Typography>

                <Typography>
                    Solltest du es dir doch noch anders überlegen, sag uns gern per WhatsApp Bescheid,
                    wir würden uns trotzdem sehr freuen, dich noch begrüßen zu dürfen!
                </Typography>
            </Box>
        </Slide>
    </Box>;
}